import React, { useState } from "react";
import { X, Plus, Edit2, Trash2, Check } from "lucide-react";
import { createLabel, updateLabel, deleteLabel } from "../services/labelService";

function LabelManager({ isOpen, onClose, labels, userId, onLabelsUpdate }) {
  const [labelName, setLabelName] = useState("");
  const [labelColor, setLabelColor] = useState("#3B82F6");
  const [editingLabel, setEditingLabel] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const predefinedColors = [
    "#EF4444", // red
    "#10B981", // green
    "#F59E0B", // yellow
    "#3B82F6", // blue
  ];

  const handleCreateLabel = async () => {
    if (!labelName.trim()) return;

    setIsCreating(true);
    try {
      const newLabel = await createLabel(userId, labelName.trim(), labelColor);
      onLabelsUpdate([...labels, newLabel]);
      setLabelName("");
      setLabelColor("#3B82F6");
    } catch (error) {
      alert("Failed to create category. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateLabel = async () => {
    if (!editingLabel || !labelName.trim()) return;

    try {
      await updateLabel(editingLabel.id, labelName.trim(), labelColor);
      const updatedLabels = labels.map((label) =>
        label.id === editingLabel.id
          ? { ...label, name: labelName.trim(), color: labelColor }
          : label
      );
      onLabelsUpdate(updatedLabels);
      setEditingLabel(null);
      setLabelName("");
      setLabelColor("#3B82F6");
    } catch (error) {
      alert("Failed to update category. Please try again.");
    }
  };

  const handleDeleteLabel = async (labelId) => {
    if (!confirm("Are you sure you want to delete this category? It will be removed from all cases.")) {
      return;
    }

    try {
      await deleteLabel(labelId);
      const updatedLabels = labels.filter((label) => label.id !== labelId);
      onLabelsUpdate(updatedLabels);
    } catch (error) {
      alert("Failed to delete category. Please try again.");
    }
  };

  const startEditingLabel = (label) => {
    setEditingLabel(label);
    setLabelName(label.name);
    setLabelColor(label.color);
  };

  const cancelEditing = () => {
    setEditingLabel(null);
    setLabelName("");
    setLabelColor("#3B82F6");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[1100] p-4">
      <div className="bg-[#1e1b4b] text-gray-300 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-yellow-500">Manage Categories</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Create/Edit Section */}
          <div className="bg-[#2d2a5a] p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400 mb-3">
              {editingLabel ? "Edit Category" : "Create New Category"}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Category Name</label>
                <input
                  type="text"
                  value={labelName}
                  onChange={(e) => setLabelName(e.target.value)}
                  placeholder="e.g., North Case"
                  className="w-full px-3 py-2 bg-[#1e1b4b] border border-gray-600 rounded text-sm text-white focus:outline-none focus:border-yellow-500"
                  maxLength={20}
                />
              </div>

              <div>
                <label className="text-xs text-gray-400 mb-2 block">Category Color</label>
                <div className="flex gap-2 mb-2">
                  {predefinedColors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setLabelColor(color)}
                      className={`w-8 h-6 rounded transition-all ${
                        labelColor === color
                          ? "ring-2 ring-yellow-500 ring-offset-2 ring-offset-[#2d2a5a]"
                          : "hover:scale-110"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={labelColor}
                    onChange={(e) => setLabelColor(e.target.value)}
                    className="w-10 h-6 rounded cursor-pointer bg-transparent border border-gray-600"
                  />
                  <span className="text-xs text-gray-400">Custom color</span>
                </div>
              </div>

              {editingLabel ? (
                <div className="flex gap-2">
                  <button
                    onClick={handleUpdateLabel}
                    disabled={!labelName.trim()}
                    className="flex-1 flex items-center justify-center gap-2 bg-yellow-500 text-gray-900 px-4 py-2 rounded font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Check size={16} />
                    Update
                  </button>
                  <button
                    onClick={cancelEditing}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleCreateLabel}
                  disabled={!labelName.trim() || isCreating}
                  className="w-full flex items-center justify-center gap-2 bg-yellow-500 text-gray-900 px-4 py-2 rounded font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus size={16} />
                  {isCreating ? "Creating..." : "Create Category"}
                </button>
              )}
            </div>
          </div>

          {/* Existing Labels */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 mb-2">Your Categories</h3>
            {labels.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No categories created yet. Create your first category above!
              </p>
            ) : (
              <div className="space-y-2">
                {labels.map((label) => (
                  <div
                    key={label.id}
                    className="flex items-center justify-between bg-[#2d2a5a] p-3 rounded-lg hover:bg-[#3d3a6a] transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div
                        className="w-6 h-6 rounded flex-shrink-0"
                        style={{ backgroundColor: label.color }}
                      />
                      <span className="text-sm text-white truncate">{label.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEditingLabel(label)}
                        className="p-1.5 text-gray-400 hover:text-yellow-500 transition-colors"
                        title="Edit Category"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteLabel(label.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                        title="Delete Category"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LabelManager;
